/* @flow */

import { css, names } from 'linaria';
import React, { Component } from 'react';

import Colors from '../../constants/Colors';
import MediaQueries from '../../constants/MediaQueries';
import { responsiveStyles } from '../../constants/Responsive';
import type { ResponsiveValue } from '../../constants/Responsive';
import {
  RESPONSIVE_FONT_SIZE_STYLES,
  RESPONSIVE_TEXT_ALIGN_STYLES,
} from '../../constants/ResponsiveText';

type FontSize =
  | '18'
  | '20'
  | '21'
  | '22'
  | '24'
  | '26'
  | '28'
  | '32'
  | '40'
  | '44'
  | '48'
  | '56'
  | '80';

const FONT_SIZE_STYLES = {
  size18: css`
    font-size: 18px;
  `,
  size20: css`
    font-size: 20px;
  `,
  size21: css`
    font-size: 21px;
  `,
  size22: css`
    font-size: 20px;
    ${MediaQueries.Medium} {
      font-size: 22px;
    }
  `,
  size24: css`
    font-size: 22px;
    ${MediaQueries.Medium} {
      font-size: 24px;
    }
  `,
  size26: css`
    font-size: 24px;
    ${MediaQueries.Medium} {
      font-size: 26px;
    }
  `,
  size28: css`
    font-size: 20px;
    ${MediaQueries.Medium} {
      font-size: 28px;
    }
  `,
  size32: css`
    font-size: 28px;
    ${MediaQueries.Medium} {
      font-size: 32px;
    }
  `,
  size40: css`
    font-size: 32px;
    ${MediaQueries.Medium} {
      font-size: 40px;
    }
  `,
  size44: css`
    font-size: 28px;
    ${MediaQueries.Medium} {
      font-size: 32px;
    }
    ${MediaQueries.Large} {
      font-size: 44px;
    }
  `,
  size48: css`
    font-size: 30px;
    ${MediaQueries.Medium} {
      font-size: 34px;
    }
    ${MediaQueries.Large} {
      font-size: 48px;
    }
  `,
  size56: css`
    font-size: 40px;
    ${MediaQueries.Medium} {
      font-size: 44px;
    }
    ${MediaQueries.Large} {
      font-size: 56px;
    }
  `,
  size80: css`
    font-size: 40px;
    ${MediaQueries.Medium} {
      font-size: 80px;
    }
  `,
};

const FONT_WEIGHT_STYLES = {
  light: css`
    font-weight: 400;
  `,
  book: css`
    font-weight: 400;
  `,
  medium: css`
    font-weight: 500;
  `,
  bold: css`
    font-weight: 700;
  `,
  black: css`
    font-weight: 900;
  `,
};

const HTML_TAGS = {
  '18': 'h6',
  '20': 'h6',
  '21': 'h6',
  '22': 'h5',
  '24': 'h4',
  '26': 'h4',
  '28': 'h4',
  '32': 'h3',
  '40': 'h2',
  '44': 'h2',
  '48': 'h2',
  '56': 'h1',
  '64': 'h1',
  '80': 'h1',
};

const LINE_HEIGHT_STYLES = {
  lh18: css`
    line-height: 1.125;
  `,
  lh20: css`
    line-height: 1.125;
  `,
  lh21: css`
    line-height: 1.125;
  `,
  lh22: css`
    line-height: 1.125;
  `,
  lh24: css`
    line-height: 1.125;
  `,
  lh26: css`
    line-height: 1.125;
  `,
  lh28: css`
    line-height: 1.125;
  `,
  lh32: css`
    line-height: 1.125;
  `,
  lh40: css`
    line-height: 1.125;
  `,
  lh44: css`
    line-height: 1.125;
  `,
  lh48: css`
    line-height: 1.125;
  `,
  lh56: css`
    line-height: 1;
  `,
  lh64: css`
    line-height: 1;
  `,
  lh80: css`
    line-height: 1;
  `,
};

const TEXT_ALIGN_STYLES = {
  left: css`
    text-align: left;
  `,
  right: css`
    text-align: right;
  `,
  center: css`
    text-align: center;
  `,
};

class Title extends Component<{
  size: FontSize | ResponsiveValue<FontSize>,
  weight: $Keys<typeof FONT_WEIGHT_STYLES>,
  color: string,
  align?: $Keys<typeof TEXT_ALIGN_STYLES> | ResponsiveValue<$Keys<typeof TEXT_ALIGN_STYLES>>,
  style?: any,
  className?: string,
  children?: any,
}> {
  static defaultProps = {
    color: Colors.Black,
    size: '22',
    weight: 'book',
  };

  render() {
    let { size, color, align, children, weight, style, className } = this.props;
    const medianSize = typeof size === 'string' ? size : size.large || size.medium || size.small;

    let Tag = HTML_TAGS[medianSize];
    let fontSizeStyle = FONT_SIZE_STYLES[`size${medianSize}`];
    let fontWeightStyle = FONT_WEIGHT_STYLES[weight];
    let lineHeightStyle = LINE_HEIGHT_STYLES[`lh${medianSize}`];
    if (process.env.NODE_ENV !== 'production') {
      if (!fontSizeStyle) console.warn(`Unsupported size ${medianSize} for Title`);
      if (!fontWeightStyle) console.warn(`Unsupported weight ${weight} for Title`);
      if (!lineHeightStyle) console.warn(`Unsupported line height ${medianSize} for Title`);
    }

    return (
      <Tag
        className={names(
          size &&
            (typeof size === 'string'
              ? FONT_SIZE_STYLES[`size${size}`]
              : responsiveStyles(size, k => RESPONSIVE_FONT_SIZE_STYLES[k])),
          fontWeightStyle,
          lineHeightStyle,
          align &&
            (typeof align === 'string'
              ? TEXT_ALIGN_STYLES[align]
              : responsiveStyles(align, k => RESPONSIVE_TEXT_ALIGN_STYLES[k])),
          className
        )}
        style={{ color, ...style }}>
        {children}
      </Tag>
    );
  }
}

export default Title;



// WEBPACK FOOTER //
// ./src/ui/components/core/Title.js